import * as React from 'react';

import { connect } from 'react-redux';

import { Layout, Spin, Row, Col, Card, Table } from 'antd';
import {
	analyticsNow,
	dateFormatter,
	getWorkAnalyticsPrioritiesDrillDownLink,
} from '../../utils/DataFormatterUtils';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import { debounce } from '../../utils/PerformanceUtils';
import { EmptyState } from '../empty_state/EmptyState';
import WorkPriorityChartCard from './WorkPriorityChartCard';
import moment from 'moment';
import {
	clearWorkPriorityFilters,
	updateWorkPriorityFilters,
} from '../../actions/work_analytics_actions';
import {
	fetchWorkOrderCountByDay,
	fetchWorkOrderCountByMonth,
	fetchWorkOrderCountByWeek,
} from '../../thunks/work_analytics_thunks';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../thunks/work_order_priorities_thunks';
import {
	ANALYTICS_DEFAULT_TIME_ZONE,
	CSV_EXPORT_DEFAULTS,
	ROLE_TYPES,
} from '../../utils/DataConstants';
import { ExportToCsv } from 'export-to-csv';
import { Link } from 'react-router-dom';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

const { Content } = Layout;
require('color');
require('./WorkAnalyticsOverviewPrioritiesPage.less');
const isEqual = require('fast-deep-equal');

const defaultSortOrder: 'ascend' | 'descend' = 'descend';

// These week/day breakdowns override the control bar's start date, so the override has to be
// anchored to the same zone the filters carry — the browser's calendar would ask for a different
// month depending on where the user sits. Falls back to UTC, which is what the param builder uses.
const priorityFiltersTimeZone = (analytics) =>
	nullSafeGetOrElse('priorityFilters.timeZone', analytics, ANALYTICS_DEFAULT_TIME_ZONE);

class WorkAnalyticsOverviewPrioritiesPage extends React.Component<any, any> {
	state = {
		initialLoading: true,
	};
	debouncedCallback = debounce(
		() => {
			const {
				fetchWorkOrderCountsByMonth,
				fetchWorkOrderCountsByWeek,
				fetchWorkOrderCountsByDay,
				analytics,
			} = this.props;
			const { initialLoading } = this.state;
			if (analytics.priorityFilters.startDate && analytics.priorityFilters.endDate) {
				const promise1 = fetchWorkOrderCountsByMonth(analytics.priorityFilters);
				const promise2 = fetchWorkOrderCountsByWeek({
					...analytics.priorityFilters,
					startDate: analyticsNow(priorityFiltersTimeZone(analytics)).subtract(1, 'months'),
				});
				const promise3 = fetchWorkOrderCountsByDay({
					...analytics.priorityFilters,
					startDate: analyticsNow(priorityFiltersTimeZone(analytics)).subtract(1, 'months'),
				});
				if (initialLoading) {
					Promise.all([promise1, promise2, promise3]).then(() =>
						this.setState({ initialLoading: false })
					);
				}
			}
		},
		500,
		false
	);

	handlePrint = () =>
		exportAnalyticsPageAsPdf(
			'.analyticsOverviewFacilitiesPage',
			'work-analytics-priorities-' + new Date().toISOString().slice(0, 10) + '.pdf'
		);

	componentDidMount() {
		const {
			fetchWorkOrderCountsByMonth,
			fetchWorkOrderCountsByWeek,
			fetchWorkOrderCountsByDay,
			fetchWorkOrderPriorities,
			analytics,
		} = this.props;
		const { initialLoading } = this.state;
		const promise4 = fetchWorkOrderPriorities({});
		if (analytics.priorityFilters.startDate && analytics.priorityFilters.endDate) {
			const promise1 = fetchWorkOrderCountsByMonth(analytics.priorityFilters);
			const promise2 = fetchWorkOrderCountsByWeek({
				...analytics.priorityFilters,
				startDate: analyticsNow(priorityFiltersTimeZone(analytics)).subtract(1, 'months'),
			});
			const promise3 = fetchWorkOrderCountsByDay({
				...analytics.priorityFilters,
				startDate: analyticsNow(priorityFiltersTimeZone(analytics)).subtract(1, 'months'),
			});
			if (initialLoading) {
				Promise.all([promise1, promise2, promise3, promise4]).then(() =>
					this.setState({ initialLoading: false })
				);
			}
		}
	}

	componentDidUpdate(prevProps) {
		const { analytics } = this.props;
		if (!isEqual(prevProps.analytics.priorityFilters, analytics.priorityFilters)) {
			this.debouncedCallback();
		}
	}

	getWOPriorityNames = () => {
		const { workOrderPriorities } = this.props;
		return getObjectValues(workOrderPriorities.records).map((el) => el.name);
	};

	getColumns = () => {
		const { analytics, userType } = this.props;
		const { priorityFilters } = analytics;
		return (
			[
				{
					title: 'Month',
					dataIndex: 'month',
					render: (text) => dateFormatter(text, 'MMM YYYY'),
					defaultSortOrder: defaultSortOrder,
					sorter: (a, b) => {
						return moment(a.month).unix() - moment(b.month).unix();
					},
				},
			]
				// @ts-ignore
				.concat(
					(this.getWOPriorityNames() as any).map((wop, i) => ({
						title: wop,
						render: (data) => {
							const filters = {
								...priorityFilters,
								woPriorityIds: [nullSafeGetOrElse(`${wop}.id`, data, 0)],
							};
							return (
								<Link
									to={getWorkAnalyticsPrioritiesDrillDownLink(userType, filters, data.month, [
										'startDate,endDate',
									])}
									target="_blank"
								>
									<span className={'DrillDownButton flex'}>
										<span>{nullSafeGetOrElse(`${wop}.value`, data, 0)}</span>
										<span className={'ClickOffIcon flex'}>
											<i className="icons8-font icons8-external-link ml-2 inline-block" />
										</span>
									</span>
								</Link>
							);
						},
					}))
				)
		);
	};

	getDisplayData = () => {
		const displayData = nullSafeGetOrElse(
			'props.analytics.workOrderCountByMonth.data',
			this,
			[]
		).map((row) => {
			let el = { month: row.yearMonthKey };
			row.countByPriority.forEach((count, i) => {
				const key = nullSafeGetOrElse(
					'workOrderPriority.name',
					count,
					nullSafeGetOrElse('workOrderPriorityId', count, i)
				);
				const value = nullSafeGet('workOrderCount', count);
				el[key] = { value, id: nullSafeGet('workOrderPriorityId', count) };
			});
			return el;
		});
		return displayData;
	};

	onDownloadAnalytics = () => {
		const title = 'Spend Analytics by Priorities';
		const headers = this.getColumns().map((_) => _.title);
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: title,
			title,
			headers,
		});
		csvExporter.generateCsv(
			this.getDisplayData().map((obj) =>
				headers.reduce((acc, key) => {
					return {
						...acc,
						[key]:
							key.toLowerCase() === 'month'
								? dateFormatter(obj.month, 'MMM YYYY')
								: nullSafeGetOrElse(`${key}.value`, obj, 0),
					};
				}, {})
			)
		);
	};

	render() {
		const { saveWorkPriorityFilters, clearAllWorkPriorityFilters, analytics, workOrderPriorities } =
			this.props;
		const { initialLoading } = this.state;
		const { workOrderCountByMonth, workOrderCountByWeek, workOrderCountByDay, priorityFilters } =
			analytics;

		const workOrderPriorityNames = this.getWOPriorityNames();

		const latestWeekData = workOrderCountByWeek.data[workOrderCountByWeek.data.length - 1];
		const previousWeekData = workOrderCountByWeek.data[workOrderCountByWeek.data.length - 2];
		const last14DaysData = workOrderCountByDay.data.slice(-14);
		let workOrderPriorityCounts = [];
		if (latestWeekData && previousWeekData) {
			workOrderPriorityCounts = getObjectValues(workOrderPriorities.records).map((wop) => {
				const latestWeek = latestWeekData.countByPriority.find(
					(d) => d.workOrderPriorityId === wop.id
				) || { workOrderCount: 0 };
				const previousWeek = previousWeekData.countByPriority.find(
					(d) => d.workOrderPriorityId === wop.id
				) || { workOrderCount: 0 };
				const last14Days = last14DaysData.map((el) => {
					const workOrderCount = (
						el.countByPriority.find((d) => d.workOrderPriorityId === wop.id) || {
							workOrderCount: 0,
						}
					).workOrderCount;
					return { x: moment(el.date).format('YYYY-MM-DD'), y: workOrderCount };
				});
				return {
					id: wop.id,
					latestWeekCount: latestWeek.workOrderCount,
					difference: latestWeek.workOrderCount - previousWeek.workOrderCount,
					percentChange:
						previousWeek.workOrderCount === 0
							? 0
							: (latestWeek.workOrderCount - previousWeek.workOrderCount) /
							  (1.0 * previousWeek.workOrderCount),
					displayName: wop.name,
					last14Days,
				};
			});
		}

		const getTrendIndicator = (percentageChange) => {
			if (percentageChange === 0) {
				return null;
			}
			return percentageChange > 0 ? 'up' : 'down';
		};

		const tableData = this.getDisplayData();

		return (
			<Content
				className="analyticsOverviewFacilitiesPage"
				style={{ padding: '0 0.5em', position: 'relative' }}
			>
				<div className="analytics-pdf-only" style={{ display: 'none' }}>
					<h2>Work Analytics – Priorities</h2>
				</div>
				{/*<BackTop/>*/}
				<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
					<Col span={24}>
						<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<div></div>
							<AnalyticsControlBar
								updateFilters={saveWorkPriorityFilters}
								clearFilters={clearAllWorkPriorityFilters}
								supplierFacilityIds={priorityFilters.supplierFacilityIds}
								woPriorityIds={priorityFilters.woPriorityIds}
								locationIds={priorityFilters.locationIds}
								brandIds={priorityFilters.brandIds}
								startDate={priorityFilters.startDate}
								endDate={priorityFilters.endDate}
								isPM={priorityFilters.isPM}
								workCompletedAtStartDate={priorityFilters.workCompletedAtStartDate}
								workCompletedAtEndDate={priorityFilters.workCompletedAtEndDate}
								showDownload
								onDownload={this.onDownloadAnalytics}
								showPrint={true}
								onPrint={this.handlePrint}
								filterConfig={{
									isPM: true,
									workCompletedAt: true,
									workOrderPriorities: true,
									brandIds: true,
								}}
							/>
						</div>
					</Col>
				</Row>
				<Row style={{ margin: '0.5em -8px' }} gutter={16}>
					<Col span={24}>
						{workOrderCountByMonth.data && workOrderCountByMonth.data.length > 0 ? (
							<>
								<WorkPriorityChartCard
									data={workOrderCountByMonth.data}
									loading={!!workOrderCountByMonth.loading || initialLoading}
									priorities={getObjectValues(workOrderPriorities.records) as any}
									filters={priorityFilters}
									userType={(this.props as any).userType}
								/>
								<Card bodyStyle={{ padding: '17.5px 24px', marginTop: 16 }}>
									<Spin spinning={workOrderCountByMonth.loading || initialLoading}>
										<Table
											pagination={{ pageSize: 12 }}
											columns={this.getColumns()}
											rowKey={(el) => el['category']}
											dataSource={workOrderPriorityNames.length > 0 ? tableData : []}
										/>
									</Spin>
								</Card>
							</>
						) : (
							<Card bodyStyle={{ padding: '17.5px 24px' }}>
								<Spin spinning={workOrderCountByMonth.loading || initialLoading}>
									<EmptyState
										height={120}
										headline={'No work orders found'}
										body={
											<div style={{ maxWidth: 488, textAlign: 'center' }}>
												We didn't find any work orders. Try changing your filters or expanding the
												selected time period.
											</div>
										}
									/>
								</Spin>
							</Card>
						)}
					</Col>
				</Row>
			</Content>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	accountActivated: state.session.accountActivated,
	workOrderPriorities: state.work_order_priorities,
	currentUser: state.session.currentUser,
	analytics: state.work_analytics,
	userType: (state as any).session.userType,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveWorkPriorityFilters: (filters) => dispatch(updateWorkPriorityFilters(filters)),
	clearAllWorkPriorityFilters: () => dispatch(clearWorkPriorityFilters()),
	fetchWorkOrderPriorities: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier.read(params, filters, pagination, sorting)
				: workOrderPrioritiesRestCrudThunksForBuyer.read(params, filters, pagination, sorting)
		),
	fetchWorkOrderCountsByMonth: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchWorkOrderCountByMonth(ownProps.userType)(params, filters, pagination, sorting)),
	fetchWorkOrderCountsByWeek: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchWorkOrderCountByWeek(ownProps.userType)(params, filters, pagination, sorting)),
	fetchWorkOrderCountsByDay: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchWorkOrderCountByDay(ownProps.userType)(params, filters, pagination, sorting)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(WorkAnalyticsOverviewPrioritiesPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
